import React, { useState, useRef, useEffect } from 'react';
import { KNOWN_STORES, EXTRA_COLORS, hex2rgba } from './data';
import styles from './StoreManager.module.css';

export default function StoreManager({ stores, onToggle, onRemove, onAdd }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [customName, setCustomName]     = useState('');
  const [banner, setBanner]             = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const addedIds  = stores.map(s => s.id);
  const available = KNOWN_STORES.filter(s => !addedIds.includes(s.id));

  const handleSelectKnown = (store) => {
    onAdd(store.name, store);
    setDropdownOpen(false);
    if (!store.official) setBanner({ name: store.name });
  };

  const handleAddCustom = () => {
    const name = customName.trim();
    if (!name) return;
    const usedColors = stores.map(s => s.color);
    const color = EXTRA_COLORS.find(c => !usedColors.includes(c)) || EXTRA_COLORS[0];
    onAdd(name, {
      id: name.toLowerCase().replace(/[^a-z0-9]/g, '_'),
      name, color,
      bg: hex2rgba(color, 0.1),
      official: false,
    });
    setBanner({ name });
    setCustomName('');
    setDropdownOpen(false);
  };

  return (
    <div>
      <div className={styles.bar}>
        <span className={styles.label}>Stores:</span>

        {stores.map((s) => {
          const isOfficial = s.official !== false;
          return (
            <div
              key={s.id}
              className={[
                isOfficial ? styles.chip : styles.chipUnofficial,
                s.active ? '' : styles.inactive,
              ].join(' ')}
              style={{ color: s.color, background: s.bg, borderColor: s.color + '55' }}
              onClick={() => onToggle(s.id)}
              title={isOfficial ? s.name : `${s.name} — estimated prices only`}
            >
              {s.name}
              {!isOfficial && <span className={styles.betaBadge}>beta</span>}
              <span
                className={styles.remove}
                onClick={(e) => { e.stopPropagation(); onRemove(s.id); setBanner(null); }}
              >
                ×
              </span>
            </div>
          );
        })}

        {/* Dropdown */}
        <div className={styles.addWrap} ref={dropdownRef}>
          <button className={styles.addBtn} onClick={() => setDropdownOpen(o => !o)}>
            + Add store <span className={styles.caret}>{dropdownOpen ? '▲' : '▼'}</span>
          </button>

          {dropdownOpen && (
            <div className={styles.dropdown}>
              {available.filter(s => s.official).length > 0 && (
                <>
                  <div className={styles.dropSection}>Supported</div>
                  {available.filter(s => s.official).map(s => (
                    <div key={s.id} className={styles.dropItem} onClick={() => handleSelectKnown(s)}>
                      <span className={styles.dropDot} style={{ background: s.color }} />
                      {s.name}
                    </div>
                  ))}
                </>
              )}

              {available.filter(s => !s.official).length > 0 && (
                <>
                  <div className={styles.dropSection}>Coming soon</div>
                  {available.filter(s => !s.official).map(s => (
                    <div key={s.id} className={styles.dropItem} onClick={() => handleSelectKnown(s)}>
                      <span className={styles.dropDot} style={{ background: s.color }} />
                      {s.name}
                      <span className={styles.dropBeta}>beta</span>
                    </div>
                  ))}
                </>
              )}

              <div className={styles.dropDivider} />
              <div className={styles.dropSection}>Custom</div>
              <div className={styles.dropCustom}>
                <input
                  type="text"
                  placeholder="Store name…"
                  value={customName}
                  onChange={e => setCustomName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddCustom()}
                  className={styles.customInput}
                  autoFocus
                />
                <button className={styles.customBtn} onClick={handleAddCustom}>Add</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Option A — inline banner */}
      {banner && (
        <div className={styles.banner}>
          <span className={styles.bannerIcon}>ℹ</span>
          <div className={styles.bannerText}>
            <strong>{banner.name}</strong> is not officially integrated yet — prices shown are estimated.
            We're actively working on adding it.{' '}
            <a href="mailto:support@example.com" className={styles.bannerLink}>Request integration ↗</a>
          </div>
          <button className={styles.bannerClose} onClick={() => setBanner(null)}>×</button>
        </div>
      )}
    </div>
  );
}
